import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  TablePagination,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';

interface Organization {
  id: string;
  name: string;
  code: string;
  timezone: string;
  settings: Record<string, any>;
  isActive: boolean;
  usersCount: number;
  adminsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationFormData {
  name: string;
  code: string;
  timezone: string;
  settings: Record<string, any>;
  isActive?: boolean;
  adminUser?: {
    username: string;
    email?: string;
    password: string;
    firstName: string;
    lastName: string;
  };
}

// Список часовых поясов
const timezones = [
  { value: 'UTC', label: 'UTC (Всемирное координированное время)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (МСК, UTC+3)' },
  { value: 'Europe/Kaliningrad', label: 'Europe/Kaliningrad (UTC+2)' },
  { value: 'Europe/Samara', label: 'Europe/Samara (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Asia/Yekaterinburg (UTC+5)' },
  { value: 'Asia/Omsk', label: 'Asia/Omsk (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Asia/Krasnoyarsk (UTC+7)' },
  { value: 'Asia/Irkutsk', label: 'Asia/Irkutsk (UTC+8)' },
  { value: 'Asia/Yakutsk', label: 'Asia/Yakutsk (UTC+9)' },
  { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok (UTC+10)' },
  { value: 'Asia/Magadan', label: 'Asia/Magadan (UTC+11)' },
  { value: 'Asia/Kamchatka', label: 'Asia/Kamchatka (UTC+12)' }
];

// Функция генерации кода организации
const generateOrgCode = (name: string): string => {
  // Словарь для транслитерации русских букв
  const translitMap: { [key: string]: string } = {
    'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Е': 'E', 'Ё': 'E',
    'Ж': 'ZH', 'З': 'Z', 'И': 'I', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M',
    'Н': 'N', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U',
    'Ф': 'F', 'Х': 'H', 'Ц': 'TS', 'Ч': 'CH', 'Ш': 'SH', 'Щ': 'SCH',
    'Ъ': '', 'Ы': 'Y', 'Ь': '', 'Э': 'E', 'Ю': 'YU', 'Я': 'YA'
  };

  return name
    .toUpperCase()
    .split('')
    .map(char => translitMap[char] || char)
    .join('')
    .replace(/[^A-Z0-9\s]/g, '') // Убираем спецсимволы
    .replace(/\s+/g, '_') // Заменяем пробелы на подчеркивания
    .substring(0, 20); // Ограничиваем длину
};

const SuperAdminOrganizationsPage: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    code: '',
    timezone: 'UTC',
    settings: {},
    adminUser: {
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: ''
    }
  });
  const [createAdmin, setCreateAdmin] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState('');

  const queryClient = useQueryClient();

  // Получение списка организаций
  const { data: organizationsData, isLoading, refetch } = useQuery(
    ['super-admin-organizations', page + 1, rowsPerPage, search],
    async () => {
      const response = await apiClient.get('/api/super-admin/organizations', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined
        }
      });
      return response.data.data;
    },
    {
      retry: 1
    }
  );

  // Создание организации
  const createMutation = useMutation(
    async (data: OrganizationFormData) => {
      const response = await apiClient.post('/api/super-admin/organizations', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['super-admin-organizations']);
        handleClose();
        setError('');
      },
      onError: (error: any) => {
        setError(error.response?.data?.message || 'Ошибка при создании организации');
      }
    }
  );

  // Обновление организации
  const updateMutation = useMutation(
    async ({ id, data }: { id: string; data: Partial<OrganizationFormData> }) => {
      const response = await apiClient.put(`/api/super-admin/organizations/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['super-admin-organizations']);
        handleClose();
        setError('');
      },
      onError: (error: any) => {
        setError(error.response?.data?.message || 'Ошибка при обновлении организации');
      }
    }
  );

  // Удаление организации
  const deleteMutation = useMutation(
    async (id: string) => {
      await apiClient.delete(`/api/super-admin/organizations/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['super-admin-organizations']);
      },
      onError: (error: any) => {
        setError(error.response?.data?.message || 'Ошибка при удалении организации');
      }
    }
  );

  const handleOpen = (org?: Organization) => {
    if (org) {
      setEditingOrg(org);
      setFormData({
        name: org.name,
        code: org.code,
        timezone: org.timezone,
        settings: org.settings,
        isActive: org.isActive
      });
    } else {
      setEditingOrg(null);
      setFormData({
        name: '',
        code: '',
        timezone: 'Europe/Moscow',
        settings: {}
      });
    }
    setError('');
    setOpen(true);
  };

  // Обработчик изменения названия организации (автогенерация кода)
  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      code: editingOrg ? prev.code : generateOrgCode(name)
    }));
  };

  const handleClose = () => {
    setOpen(false);
    setEditingOrg(null);
    setError('');
    setCreateAdmin(false);
    setFormData({
      name: '',
      code: '',
      timezone: 'UTC',
      settings: {},
      adminUser: {
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: ''
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingOrg) {
      updateMutation.mutate({
        id: editingOrg.id,
        data: formData
      });
    } else {
      const submitData = createAdmin ? formData : { ...formData, adminUser: undefined };
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (org: Organization) => {
    if (window.confirm(`Вы уверены, что хотите удалить организацию "${org.name}"?`)) {
      deleteMutation.mutate(org.id);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    refetch();
  };

  const organizations = organizationsData?.organizations || [];
  const pagination = organizationsData?.pagination || { total: 0, totalPages: 0 };

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок и статистика */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Управление организациями
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Создание и управление организациями в системе ELEKTROM
        </Typography>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{pagination.total}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Всего организаций
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">
                    {organizations.reduce((sum, org) => sum + org.usersCount, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Всего пользователей
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <AdminIcon sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h6">
                    {organizations.reduce((sum, org) => sum + org.adminsCount, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Администраторов
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Панель управления */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1 }}>
            <TextField
              size="small"
              placeholder="Поиск по названию или коду..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 300 }}
            />
            <Button type="submit" variant="outlined">
              Найти
            </Button>
          </form>
          
          <Tooltip title="Обновить">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Добавить организацию
          </Button>
        </Box>
      </Paper>

      {/* Таблица организаций */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Код</TableCell>
              <TableCell>Часовой пояс</TableCell>
              <TableCell>Пользователи</TableCell>
              <TableCell>Админы</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Дата создания</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Загрузка...</TableCell>
              </TableRow>
            ) : organizations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Нет организаций</TableCell>
              </TableRow>
            ) : (
              organizations.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {org.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={org.code} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{org.timezone}</TableCell>
                  <TableCell>
                    <Chip 
                      label={org.usersCount} 
                      size="small" 
                      color={org.usersCount > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={org.adminsCount} 
                      size="small" 
                      color={org.adminsCount > 0 ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={org.isActive ? 'Активна' : 'Неактивна'}
                      color={org.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(org.createdAt).toLocaleDateString('ru-RU')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Редактировать">
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(org)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(org)}
                        disabled={org.usersCount > 0}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} из ${count !== -1 ? count : `более ${to}`}`
          }
        />
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingOrg ? 'Редактировать организацию' : 'Создать организацию'}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Название организации"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              margin="normal"
              helperText="Код будет сгенерирован автоматически"
            />
            
            <TextField
              fullWidth
              label="Код организации"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
              margin="normal"
              disabled={!editingOrg}
              helperText={editingOrg ? "Можно изменить для существующей организации" : "Генерируется автоматически"}
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Часовой пояс</InputLabel>
              <Select
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                label="Часовой пояс"
              >
                {timezones.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {editingOrg && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive ?? true}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Активна"
                sx={{ mt: 2 }}
              />
            )}

            {!editingOrg && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={createAdmin}
                      onChange={(e) => setCreateAdmin(e.target.checked)}
                    />
                  }
                  label="Создать администратора"
                  sx={{ mt: 2 }}
                />

                {createAdmin && (
                  <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Данные администратора
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Имя"
                          value={formData.adminUser?.firstName || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            adminUser: { ...formData.adminUser!, firstName: e.target.value }
                          })}
                          required={createAdmin}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Фамилия"
                          value={formData.adminUser?.lastName || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            adminUser: { ...formData.adminUser!, lastName: e.target.value }
                          })}
                          required={createAdmin}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Логин"
                          value={formData.adminUser?.username || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            adminUser: { ...formData.adminUser!, username: e.target.value }
                          })}
                          required={createAdmin}
                          margin="normal"
                          helperText="Только латинские буквы, цифры и подчеркивания"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={formData.adminUser?.email || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            adminUser: { ...formData.adminUser!, email: e.target.value }
                          })}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Пароль"
                          type="password"
                          value={formData.adminUser?.password || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            adminUser: { ...formData.adminUser!, password: e.target.value }
                          })}
                          required={createAdmin}
                          margin="normal"
                          helperText="Минимум 6 символов"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Отмена</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {editingOrg ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SuperAdminOrganizationsPage;
